import { Injectable, InjectionToken, Type, inject } from '@angular/core';
import { SalesInsightsActivityCalendarComponent } from '../components/sales-insights-activity-calendar/sales-insights-activity-calendar.component';
import { SalesInsightsActivityListComponent } from '../components/sales-insights-activity-list/sales-insights-activity-list.component';
import { SalesInsightsBarChartComponent } from '../components/sales-insights-bar-chart/sales-insights-bar-chart.component';
import { SalesInsightsBaseComponent } from '../components/sales-insights-base/sales-insights-base.component';
import { SalesInsightsCustomerHealthComponent } from '../components/sales-insights-customer-health/sales-insights-customer-health.component';
import { SalesInsightsDonutComponent } from '../components/sales-insights-donut/sales-insights-donut.component';
import { SalesInsightsGoalProgressComponent } from '../components/sales-insights-goal-progress/sales-insights-goal-progress.component';
import { SalesInsightsKpiComponent } from '../components/sales-insights-kpi/sales-insights-kpi.component';
import { SalesInsightsLineChartComponent } from '../components/sales-insights-line-chart/sales-insights-line-chart.component';
import { SalesInsightsOrderListComponent } from '../components/sales-insights-order-list/sales-insights-order-list.component';
import { SalesInsightsProgressTableComponent } from '../components/sales-insights-progress-table/sales-insights-progress-table.component';
import { SalesInsightsStatTilesComponent } from '../components/sales-insights-stat-tiles/sales-insights-stat-tiles.component';
import { SalesInsightsTableComponent } from '../components/sales-insights-table/sales-insights-table.component';

export interface SalesInsightsComponentType {
  type: string;
  component: Type<SalesInsightsBaseComponent<any>>;
  name: string;
}

export const SALES_INSIGHTS_COMPONENT_TYPES = new InjectionToken<
  Array<SalesInsightsComponentType | SalesInsightsComponentType[]>
>('SALES_INSIGHTS_COMPONENT_TYPES');

@Injectable({
  providedIn: 'root'
})
export class SalesInsightsComponentRegistry {
  private types = new Map<string, SalesInsightsComponentType>();

  constructor() {
    const extensions = inject(SALES_INSIGHTS_COMPONENT_TYPES, { optional: true }) || [];
    [...this.standardTypes(), ...extensions.flat()].forEach(type => this.register(type));
  }

  register(type: SalesInsightsComponentType): void {
    this.types.set(type.type, type);
  }

  get(type: string): Type<SalesInsightsBaseComponent<any>> | null {
    return this.types.get(type)?.component || null;
  }

  getAll(): SalesInsightsComponentType[] {
    return Array.from(this.types.values());
  }

  protected standardTypes(): SalesInsightsComponentType[] {
    return [
      { type: 'kpi', component: SalesInsightsKpiComponent, name: 'KPI' },
      { type: 'bar-chart', component: SalesInsightsBarChartComponent, name: 'Bar chart' },
      { type: 'line-chart', component: SalesInsightsLineChartComponent, name: 'Line chart' },
      { type: 'donut', component: SalesInsightsDonutComponent, name: 'Donut' },
      { type: 'table', component: SalesInsightsTableComponent, name: 'Table' },
      { type: 'progress-table', component: SalesInsightsProgressTableComponent, name: 'Progress table' },
      { type: 'order-list', component: SalesInsightsOrderListComponent, name: 'Order list' },
      { type: 'stat-tiles', component: SalesInsightsStatTilesComponent, name: 'Stat tiles' },
      { type: 'customer-health', component: SalesInsightsCustomerHealthComponent, name: 'Customer health' },
      { type: 'goal-progress', component: SalesInsightsGoalProgressComponent, name: 'Goal progress' },
      { type: 'activity-list', component: SalesInsightsActivityListComponent, name: 'Activity list' },
      { type: 'activity-calendar', component: SalesInsightsActivityCalendarComponent, name: 'Activity calendar' }
    ];
  }
}
